import { useMemo, useState, useCallback, useEffect } from 'react'
import { useRenewal } from 'src/context/renewal'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
} from 'src/components/application/defaultComponents'
import PrebuiltRenewalFooter from 'src/components/application/PrebuiltRenewalFooter'
import BasicForm from 'src/components/application/judges/newBusiness/BasicForm'
import { ValidationError } from 'yup'
import { RenewalData } from 'src/dtos/renewalData'
import { JudgeMainInfoSchema } from 'src/yup/RenewalSchema'

const Basic = () => {
  const { renewalData } = useRenewal()
  const [basicSection, setBasicSection] = useState(renewalData?.judgeApplicationData?.basicSection ?? {})
  const [validatedFields, setValidatedFields] = useState<string[]>([])

  const validateSteps = useMemo(() => {
    const totalSections = 6
    let steps: {[key: string]: boolean} = {}
    try {
      JudgeMainInfoSchema.validateSync(basicSection, { abortEarly: false })

      for (let stepNumber = 1; stepNumber <= totalSections; stepNumber++) {
        steps[`step${stepNumber}`] = true
      }

      return steps
    } catch(err) {
      const { errors } = err as ValidationError

      for (let stepNumber = 1; stepNumber <= totalSections; stepNumber++) {
        steps[`step${stepNumber}`] = !errors.includes(`step${stepNumber}`)
      }

      return steps
    }
  }, [basicSection, JudgeMainInfoSchema])

  const updatedApplicationData = (): RenewalData => ({
    ...renewalData,
    currentPage: window.location.pathname,
    judgeApplicationData: {
      ...renewalData?.judgeApplicationData,
      basicSection: {
        ...renewalData?.judgeApplicationData?.basicSection,
        ...basicSection
      }
    }
  })

  const handleValidateForm = useCallback(() => {
    try {
      JudgeMainInfoSchema.validateSync(basicSection, { abortEarly: false })
      setValidatedFields([])
    } catch(err) {
      const { errors } = err as ValidationError
      const updatedErrorArray = Array.from(new Set(errors))

      if(JSON.stringify(validatedFields) !== JSON.stringify(updatedErrorArray)) {
        setValidatedFields(updatedErrorArray)
        if(updatedErrorArray.length > 0) {
          document.getElementById(updatedErrorArray[0])?.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }
  }, [JudgeMainInfoSchema, basicSection, validatedFields])

  useEffect(() => {
    if(Object.keys(validatedFields).length > 0) {
      handleValidateForm()
    }
  }, [handleValidateForm, validatedFields])

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle>Let’s start with your basic information.</DefaultTitle>
        <DefaultSubtitle>
          Please fill out the information below for the Judge that is the applicant.
        </DefaultSubtitle>
        <BasicForm
          judgeBasicData={basicSection}
          setJudgeBasicData={setBasicSection}
          validateSteps={validateSteps}
          validatedFields={validatedFields}
          isRenewal
        />
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/verify"
        isFormValid={Object.keys(validateSteps).every(key => validateSteps[key] === true)}
        nextRoute="/customer/renew/judge/questions/main"
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleValidateForm}
      />
    </ApplicationBodyContainer>
  )
}

export default Basic